import { Helmet } from "react-helmet-async";

export const SITE_URL = "https://www.seniorhub.care";
export const SITE_NAME = "SeniorHub";
export const DEFAULT_OG_IMAGE =
  "https://www.seniorhub.care/og-image.png";

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  noindex?: boolean;
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  keywords?: string[];
}

/** Default keywords applied to every page (SAFE acronym + core taxonomy). */
const DEFAULT_KEYWORDS = [
  "SeniorHub",
  "Smart Age-Friendly Evaluation",
  "age-friendly assessment",
  "age-friendly certification",
  "age-friendly cities",
  "ageing population",
  "elder care platform",
  "govtech",
  "healthcare maturity assessment",
];

/**
 * Per-page SEO: title, meta description, canonical, OpenGraph, Twitter, optional JSON-LD.
 * Title is truncated to <60 chars, description to <160 chars (Google guidelines).
 */
export default function SEO({
  title,
  description,
  path = "",
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  type = "website",
  jsonLd,
  keywords = [],
}: SEOProps) {
  const fullTitle = title.length > 60 ? title.slice(0, 57) + "..." : title;
  const desc = description.length > 160 ? description.slice(0, 157) + "..." : description;
  const url = `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`.replace(/\/$/, "") || SITE_URL;
  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const mergedKeywords = Array.from(new Set([...DEFAULT_KEYWORDS, ...keywords])).join(", ");

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={mergedKeywords} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large" />}

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {jsonLdArray.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}
